<template>
  <div v-if="isOpen" class="daily-overlay">

    <!-- MODAL / BOTTOM-SHEET -->
    <div class="daily-container" :class="{ 'mobile-sheet': isMobile }">

      <!-- MOBILE DRAG HANDLE -->
      <div v-if="isMobile" class="drag-handle"></div>

      <!-- CLOSE BUTTON -->
      <button class="close-btn" @click="close">✕</button>

      <!-- TITLE -->
      <h2 class="daily-title"> {{ $t('sidebar.Daily Rewards') }}</h2>

      <!-- COUNTDOWN -->
      <p v-if="!canClaim" class="countdown">
       {{ $t('sidebar.Bir sonraki ödül için') }}: <span>{{ countdownText }}</span>
      </p>

      <!-- PAGINATION -->
      <div class="pagination">
        <button class="page-btn" @click="prevPage" :disabled="page === 1">
          <i class="arrow left"></i>
        </button>

        <span class="page-text">{{ page }} / 3</span>

        <button class="page-btn" @click="nextPage" :disabled="page === 3">
          <i class="arrow right"></i>
        </button>
      </div>

      <!-- REWARD GRID -->
      <div class="reward-grid">
        <div
          v-for="day in paginatedRewards"
          :key="day.day"
          class="reward-card"
        >

          <!-- IMAGE WRAPPER -->
          <div class="reward-img-box">
            <img :src="rewardImage" class="reward-img" />

            <!-- LOCKED / CLAIMED -->
            <div
              v-if="day.day < streak || (day.day === streak && !canClaim)"
              class="reward-locked"
            >
              <img src="@/assets/img/lock.png" class="claimed-icon" />
            </div>

            <!-- TODAY BADGE -->
            <div
              v-if="day.day === todayDay"

              class="today-badge"
            >
               {{ $t('sidebar.TODAY') }}
            </div>
          </div>

          <!-- DAY TEXT -->
          <p class="reward-day"> {{ $t('sidebar.Day') }} {{ day.day }}</p>

          <!-- AMOUNT -->
          <p class="reward-amount">
            {{ convertToFiat(day.amountFiat) }} {{ fiatCurrency }}
          </p>

          <!-- CLAIM BUTTON -->
      <button
  v-if="day.day === todayDay"
  class="claim-btn"
  @click="claimReward"
  :disabled="loading || !canClaim"
>
  {{ canClaim ? $t('sidebar.claim') : $t('sidebar.wait') }}
</button>
        </div>
      </div>

      <!-- SUCCESS MESSAGE -->
      <div v-if="successInfo" class="success-box">
        🎉 +{{ successInfo.cryptoReward }} {{ coinType }} <br />
        <small>({{ convertToFiat(successInfo.fiatReward) }} {{ fiatCurrency }})</small>
      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import axios from "axios";

const props = defineProps({ isOpen: Boolean });
const emit = defineEmits(["close"]);

const rewards = ref([]);
const streak = ref(0);
const canClaim = ref(false);
const fiatCurrency = ref("USD");
const coinType = ref("BTC");
const settings = ref(null);

const loading = ref(false);
const successInfo = ref(null);
const countdownText = ref("");

const isMobile = ref(false);
const page = ref(1);

let timer = null;

const rewardImage = require("@/assets/img/sidebar/promoc.png");

onMounted(() => {
  isMobile.value = window.innerWidth <= 768;
});

// USD → fiat
const convertToFiat = (usdAmount) => {
  const fiat = fiatCurrency.value;

  if (!settings.value?.rates) return usdAmount;

  const rate = settings.value.rates[fiat];
  if (!rate) return usdAmount;

  return Number(usdAmount * rate).toFixed(2);
};

// Countdown
const startCountdown = (lastClaim) => {
  if (!lastClaim) return;

  const update = () => {
    const diff = 24 * 3600 * 1000 - (Date.now() - new Date(lastClaim).getTime());
    if (diff <= 0) {
      countdownText.value = "Hazır!";
      canClaim.value = true;
      return;
    }

    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);

    countdownText.value = `${h}h ${m}m ${s}s`;
  };

  update();
  timer = setInterval(update, 1000);
};

// Fetch
const fetchData = async () => {
  const st = await axios.get("/daily/status");
  streak.value = st.data.streak;
  canClaim.value = st.data.canClaim;

  if (!st.data.canClaim) startCountdown(st.data.lastClaim);

  const me = await axios.get("/auth/me");
  fiatCurrency.value = me.data.user.currency.fiatCurrency;
  coinType.value = me.data.user.currency.coinType;

  const s = await axios.get("/settings");
  settings.value = s.data;

  const r = await axios.get("/admin/daily-rewards/list");
  rewards.value = r.data.data;
};

// Claim
const claimReward = async () => {
  loading.value = true;
  try {
    const res = await axios.post("/daily/claim");
    successInfo.value = res.data;
    await fetchData();
  } catch (e) {
    console.error(e);
  }
  loading.value = false;
};

const todayDay = computed(() => {
  return streak.value === 0 ? 1 : streak.value;
});


// Pagination
const paginatedRewards = computed(() => {
  const start = (page.value - 1) * 9;
  return rewards.value.slice(start, start + 9);
});

const nextPage = () => page.value < 3 && page.value++;
const prevPage = () => page.value > 1 && page.value--;

const close = () => {
  emit("close");
  successInfo.value = null;
  page.value = 1;
};

watch(
  () => props.isOpen,
  (v) => {
    if (v) fetchData();
    else timer && clearInterval(timer);
  }
);
</script>

<style scoped>

/* Overlay */
.daily-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  z-index: 999999;
  display: flex;
  justify-content: center;
  align-items: center; /* desktop */
}

/* MOBILDE bottom sheet */
@media (max-width: 768px) {
  .daily-overlay {
    align-items: flex-end !important; /* ← BOTTOM SHEET ÇÖZÜMÜ */
  }
}

.mobile-sheet {
  align-self: flex-end !important;
}

/* Modal Container */
.daily-container {
  background: var(--background);
  width: 100%;
  max-width: 520px;
  border-radius: 20px;
  padding: 22px;
  position: relative;
  max-height: 88vh;
  overflow-y: auto;
  animation: fadeUp .25s ease-out;
  border: 2px solid rgba(255,255,255,0.05);
  box-shadow: 0 0 25px rgba(0,0,0,0.45);
}

/* Mobile Bottom Sheet */
.mobile-sheet {
  border-radius: 20px 20px 0 0;
  animation: slideUp .3s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.drag-handle {
  width: 55px;
  height: 6px;
  background: #555;
  border-radius: 3px;
  margin: 0 auto 12px;
}

/* Close Button */
.close-btn {
  position: absolute;
  right: 18px;
  top: 16px;
  background: none;
  color: #bbb;
  font-size: 20px;
  transition: 0.2s;
}
.close-btn:hover {
  color: white;
  transform: scale(1.1);
}

/* Title */
.daily-title {
  text-align: center;
  color: white;
  font-size: 26px;
  margin-bottom: 10px;
  font-family: var(--font-header);
}

/* Countdown */
.countdown {
  text-align: center;
  color: #FFD94D;
  font-weight: 600;
  margin-bottom: 12px;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.page-text {
  color: #fff;
  font-size: 16px;
}

.page-btn {
  background: var(--background2);
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: .2s;
}

.page-btn:not(:disabled):hover {
  background: var(--hover);
  transform: scale(1.05);
}

.page-btn:disabled {
  opacity: 0.3;
}

/* Arrow Icons */
.arrow {
  border: solid #fff;
  border-width: 0 3px 3px 0;
  padding: 4px;
}
.left { transform: rotate(135deg); }
.right { transform: rotate(-45deg); }

.reward-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 14px;
  width: 100%;
}

/* Kartların EŞİT boyutlu görünmesi için sabit yükseklik */
.reward-card {
  background: var(--background2);
  border-radius: 12px;
  position: relative;
  padding: 10px;
  text-align: center;

  height: 150px; /* 🔥 Sabit yükseklik */
  overflow: hidden;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

/* Görsel alanı */
.reward-img-box {
  width: 100%;
  height: 60px; /* 🔥 Kart yüksekliğine orantılı */
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.reward-img {
   
  width: auto;
}

/* Today badge */
.today-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: var(--buttonyellow);
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: bold;
  color: black;
}

/* Locked overlay */
.reward-locked {
  position: absolute;
  inset: 0;
   
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  z-index: 10;
}

.claimed-icon {
  width: 38px;
  opacity: 0.9;
}

/* Textler */
.reward-day {
  margin-top: 6px;
  font-size: 13px;
  color: #ccc;
}

.reward-amount {
  margin-top: 2px;
  font-size: 10px;
  font-weight: bold;
  color: #fff;
}

/* Claim button */
.claim-btn {
  margin-top: auto; /* 🔥 Kartın en altına sabitler */
  width: 100%;
  background: var(--buttonyellow);
  box-shadow: var(--buttonyellowbox);
  padding: 6px;
  border-radius: 6px;
  color: black;
  font-weight: bold;
}

/* Image */
.reward-img-box {
  position: relative;
}
.reward-img {
   
  filter: drop-shadow(0 0 6px rgba(0,0,0,0.4));
}

/* Today badge */
.today-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: linear-gradient(90deg, #FFD700, #FFEA63);
  padding: 3px 7px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: bold;
  color: black;
  box-shadow: 0 0 6px rgba(255,215,0,0.7);
}

 
.claimed-icon {
  width: 42px;
  opacity: .9;
}

/* Day + Amount */
.reward-day {
  margin-top: 6px;
  color: #ccc;
}
 

/* Claim Button */
.claim-btn {
  background: var(--buttonyellow);
  box-shadow: var(--buttonyellowbox);
  width: 100%;
  padding: 6px;
  margin-top: 6px;
  border-radius: 7px;
  font-weight: bold;
  color: black;
}
.claim-btn:disabled {
  background: #555;
  box-shadow: none;
  color: #aaa;
}

/* Success */
.success-box {
  text-align: center;
  color: #00ff97;
  font-size: 17px;
  margin-top: 20px;
}

/* Modal Animation */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

</style>
